import React from "react";
import HelpTooltip from "../HelpTooltip";

/**
 * Every explanation on the Tax pages, in one file.
 *
 * WHY THE COPY LIVES HERE AND NOT BESIDE EACH LABEL
 * -------------------------------------------------
 * Izi (31 Oct 2026): "Taxation can be complex or misunderstood or configured.
 * Add tool tips on how we use and interpret the implementation and the steps
 * where necessary." These sentences say what Lusuku files, under whose TIN, in
 * which environment, and what each number and state means. URA will read the
 * pages that carry them, and so will the ops person deciding whether a credit
 * note is safe to raise. Scattered across four pages and three dialogs they
 * cannot be reviewed as a whole and they drift from the code the moment a
 * behaviour changes. Here the whole set reads in five minutes, and
 * taxHelp.test.js holds it to the rules below.
 *
 * THE RULE FOR WRITING ONE
 * ------------------------
 * Say what the thing is, how Lusuku uses it, and what it does NOT do. Where a
 * setting is recorded but drives nothing yet, say so: a reader who believes
 * Lusuku files a vendor's sales because a pill says "Lusuku files" has been
 * misled by us. Every fact here is one the code enforces; nothing describes a
 * behaviour that does not exist.
 *
 * It mirrors views/ThreePL/partnerHelp.js and reuses components/HelpTooltip,
 * the dashboard's one tooltip. Placement follows the same idiom: the tooltip
 * sits inside the label or heading it explains, after the text.
 */

export const TAX_HELP = Object.freeze({
  // ── What a fiscal document is at Lusuku ─────────────────────────────────
  fiscalDocument: {
    title: "What Lusuku files",
    body: "A fiscal document here is Lusuku's own tax document to a vendor: the commission invoice for Lusuku's fee on an order, or the credit note that corrects it. Both are filed with URA under Lusuku's TIN. A vendor's own sales are not Lusuku's documents; each vendor files those under their own TIN.",
  },
  environment: {
    title: "TEST and LIVE",
    body: "TEST documents go to URA's sandbox. They are rehearsals, and their numbers verify nowhere. LIVE documents go to URA's production system and are real tax documents. Lusuku stays on TEST until URA signs off the acceptance test and registers the production device, and every row carries the environment it was issued in, so a sandbox number can never be read as a live one.",
  },
  submitter: {
    title: "The submitter",
    body: "Lusuku's own Fiscal Submitter service holds the EFRIS credentials, signs each document and files it with URA, then asks URA about credit note approvals on a schedule. This dashboard never talks to URA directly: it queues the work and the submitter claims it, so a document can sit as Pending for a moment before it is filed.",
  },

  // ── Identifiers ──────────────────────────────────────────────────────────
  documentNumber: {
    title: "Lusuku's document number",
    body: "Lusuku's own number for the document, worked out from the order and the vendor so a retry can never produce a second document: CINV for a commission invoice, CCN for the credit note that answers it. URA's number is separate and sits under URA number.",
  },
  fdn: {
    title: "Fiscal document number",
    body: "The number URA assigns when it accepts a document, often called the FDN. It is issued at upload, cannot be backdated and cannot be withdrawn, which is why a wrong document is corrected by a credit note rather than edited. A document without one has not been issued, whatever else the row says.",
  },
  verificationCode: {
    title: "Verification code",
    body: "URA's code for checking the document on its portal. It is printed on the document beside the QR code, and a document is only treated as issued once URA has returned both it and the fiscal document number.",
  },
  applicationReference: {
    title: "URA application reference",
    body: "URA's handle on a credit note application it has accepted but not yet approved. It is not a fiscal document number and is never shown as one. Quote it to URA when an approval has waited too long.",
  },

  // ── Money ────────────────────────────────────────────────────────────────
  amount: {
    title: "Gross and tax",
    body: "The gross is what the vendor was charged and already includes VAT at 18 percent, because Lusuku is VAT registered and commission is a standard rated service. The tax is split out of the gross the way URA does it, so 2,400 is 2,033.89 net and 366.11 tax, and net plus tax always equals the gross.",
  },

  // ── States ───────────────────────────────────────────────────────────────
  status: {
    title: "Where the document is with URA",
    body: "Pending: raised and waiting for the submitter. Submitting: the submitter holds it. Submitted: URA issued it and it has a fiscal document number. Awaiting URA approval: a credit note URA has accepted as an application and will approve by hand. Rejected: the filing was refused, by URA or by the submitter before anything was sent, and the reason is in the details. Withdrawn: a credit note URA has cancelled, or an application URA voided; the record and any number stay.",
  },
  filedOrLodged: {
    title: "Issued, filed, lodged",
    body: "Issued is when Lusuku raised the document. Filed is when URA accepted it and gave it a fiscal document number. Lodged is when URA accepted a credit note as an application; its approval, and its number, come later.",
  },
  uraDetail: {
    title: "URA's words and ours",
    body: "URA detail is what URA's system answered, in its own words. Rejection reason is the submitter's reading of why the filing was refused. They are kept apart so a reader is never told URA said something URA did not.",
  },

  // ── Credit notes ─────────────────────────────────────────────────────────
  creditNote: {
    title: "How a credit note works",
    body: "A credit note answers an invoice URA has already issued; an issued document cannot be edited or deleted. URA treats it as an application and approves it by hand, so it waits with an application reference before it gets its own fiscal document number. Two things raise one: a refund that has settled, and tax staff from this page with a reason. Both write the same document. An invoice takes one credit note, and crediting the whole amount marks the invoice void.",
  },
  creditNoteAmount: {
    title: "Whole or part",
    body: "The whole invoice reverses the commission in full and marks the invoice void. A part amount is your statement of how much to credit; the tax is worked out from it at the invoice's rate, and the invoice stays issued with the credit note netting it. A credit note cannot exceed its invoice.",
  },
  cancelCreditNote: {
    title: "Cancelling a credit note",
    body: "URA cancels an issued credit note by its fiscal document number, with a reason code from URA's list: 101 the buyer refused the document as incorrect, 102 it was not delivered because the document was incorrect, 103 other reasons. Lusuku records the request and the submitter asks URA. Nothing is shown as cancelled until URA confirms, and a credit note still awaiting approval cannot be cancelled.",
  },

  // ── EFRIS health ─────────────────────────────────────────────────────────
  healthVerdict: {
    title: "What the checks mean",
    body: "The filing checks ask whether Lusuku can file a commission document right now in the current environment: the seller identity is complete, the commission item is registered at URA, a document has reached URA, and nothing is pending, held, stuck or rejected. The production checks ask whether Lusuku files for real, and read Not yet, not failed, while the identity is still on TEST.",
  },
  sellerIdentity: {
    title: "Lusuku as the seller",
    body: "The taxpayer every Lusuku document is filed under: Lusuku's legal name, TIN, address, email and VAT registration, read from the platform's own configuration and never from a vendor. On a commission invoice Lusuku is the seller and the vendor is the buyer.",
  },
  configuredFrom: {
    title: "Configured from",
    body: "Where the seller identity is read from on this server: its environment, or the fiscal seller setting. Masked values are shown exactly as the server masked them.",
  },
  deviceNumber: {
    title: "Device number",
    body: "The device serial URA issues with the credentials. This shows whether one is recorded on the platform; the submitter holds the device and the signing certificate on its own host, so filing can work while this reads Not set. Lusuku's production device application is with URA pending the acceptance test.",
  },
  vatRegistered: {
    title: "VAT registered",
    body: "Lusuku is VAT registered, so every commission invoice and credit note carries 18 percent under URA's tax category A, and its prices to vendors are VAT inclusive.",
  },
  commissionItem: {
    title: "The commission item",
    body: "Commission is a service registered at URA under Lusuku's TIN with its own item code, commodity category and unit of measure. URA matches each filed line to that registration by code and name and refuses a line that does not match. Under URA's unit 117 the quantity on the document is the amount in shillings and the unit price is 1.00.",
  },
  documentCounters: {
    title: "The counts",
    body: "Raised is every commission document written. With an FDN is those URA has issued; Live with an FDN is those issued in production. Waiting to file is Pending with the submitter. Held is raised while Lusuku's seller identity was incomplete, so it carries no seller yet; once the identity is set, raising the commission for that order again promotes it to issued. Stuck mid-claim is a submitter claim that expired without an answer.",
  },
  settledOrders: {
    title: "Settled orders against documents",
    body: "A commission invoice is raised when an order settles, on every payment rail. This compares the orders settled in the window with the documents raised for them: a settled order with no document is a gap to repair, not a quiet period.",
  },
  vendorSalesFiled: {
    title: "Vendor sales filed",
    body: "Sales captured for filing on a vendor's behalf. Lusuku files only its own commission documents today, and the submitter holds only Lusuku's own credentials, so this reads 0 of 0 until a vendor is enrolled with URA and has authorised Lusuku to file for them.",
  },

  // ── URA classification ───────────────────────────────────────────────────
  classification: {
    title: "Codes on every line",
    body: "Every line on an EFRIS document carries URA's commodity code and unit of measure. Lusuku's own documents carry the commission item's. Vendor products are mapped here so that a receipt for a vendor's sale can carry URA's codes; mapping a product files nothing by itself.",
  },
  mappedMeaning: {
    title: "What mapped means",
    body: "A product counts as mapped when a code would resolve at filing time: its own code first, otherwise its Lusuku category's code, otherwise its unit's URA unit. So mapping one category or one unit clears every product that uses it. Partly mapped means only one of the two, the commodity code or the unit, resolves.",
  },
  commodityCode: {
    title: "URA commodity code",
    body: "URA's code for what is sold, picked from URA's own commodity list held on this server. A code that is not on the list is refused, because URA would refuse the document that carried it.",
  },
  uraUnit: {
    title: "URA unit of measure",
    body: "URA's unit for the line, picked from URA's own unit dictionary held on this server. A product with no unit of its own inherits the URA unit mapped on its Lusuku unit.",
  },
  taxCategories: {
    title: "URA's tax categories",
    body: "URA's eleven tax category codes, and how each writes its rate on a document: 0.18 for standard, 0 for zero rated, a dash for exempt. The VAT category set on a product decides which code its line files under. Lusuku's own commission is standard rated, code 01. Code 11, VAT not applicable, is refused by URA, so the submitter refuses the line before sending it.",
  },

  // ── Vendor tax settings ──────────────────────────────────────────────────
  vatCharging: {
    title: "Charging or not charging VAT",
    body: "Whether the vendor's sales carry VAT. A Lusuku super user records it on the vendor's contract under Wallet, Commission. A vendor charges VAT only when they are marked VAT registered and their VAT registration number (TIN) is on file, so a registered vendor with no TIN charges no VAT until one is added. Charging means 18 percent sits inside the vendor's prices and on the receipt lines for their sales; not charging means their lines carry no VAT.",
  },
  efrisEnrolment: {
    title: "Enrolment with URA",
    body: "Where the vendor has got to with URA, as ops records it. Not enrolled: nothing recorded. Pending: the vendor has applied and URA's reference is in the note. Active: URA has issued credentials and the device number is recorded. Suspended: URA has suspended them. Each vendor enrols on URA's portal under their own TIN; Lusuku does not enrol for them.",
  },
  vendorEnvironment: {
    title: "The vendor's environment",
    body: "Whether documents for this vendor would go to URA's sandbox or to production. A vendor stays on TEST until URA has issued their credentials and a test document has gone through cleanly; only a vendor enrolled Active can be switched to LIVE.",
  },
  whoFiles: {
    title: "Who files",
    body: "Vendor files means the vendor is responsible for filing their own sales with URA under their own TIN. Lusuku files records that the vendor has authorised Lusuku to submit under their TIN. That authorisation is recorded and nothing more today: the submitter holds only Lusuku's own credentials, so no document is filed under a vendor's TIN.",
  },
  nextStep: {
    title: "Next step",
    body: "The vendor's next step with URA, decided on the server from their enrolment, device number and environment, so the strip above, this filter and each row's button always agree. Record the application, add the credentials, go live, or update a vendor already filing live.",
  },
  tin: {
    title: "TIN",
    body: "The taxpayer identification number a document is filed under. It is not the same field as the VAT registration number, though for a registered taxpayer it is usually the same digits, and a vendor's VAT number is used when no TIN has been recorded.",
  },

  // ── The vendor's own view ────────────────────────────────────────────────
  vendorCoverage: {
    title: "URA codes on your catalogue",
    body: "How many of your products carry URA's commodity code and unit of measure, which a receipt for your sale would need to carry. Lusuku staff map them against URA's own lists; you do not set them, and nothing is filed on your behalf.",
  },
});

/**
 * The one way the Tax pages show a tooltip. Renders nothing for an unknown
 * topic, so a typo is invisible rather than an empty bubble; taxHelp.test.js
 * checks every topic the pages name exists here.
 */
const TaxHelp = ({ topic, placement = "top" }) => {
  const entry = TAX_HELP[topic];
  if (!entry) return null;
  return <HelpTooltip title={entry.title} body={entry.body} placement={placement} />;
};

export default TaxHelp;
